import editorialStyle from "../../../components/editorial/style.scss";
import style from "./style.scss";

import { mergeClasses } from "../../../components/helpers";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  StickyButtons,
  LargeViewOnly,
  SmallViewOnly,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import EcoFlowCouponCode from "../components/coupon-code";
import ImageSet from "../../../components/image-set";
import Image from "../../../components/image";
import Author from "../../../components/author";
import NoWrap from "../../../components/no-wrap";
import Warning from "../../../components/warning";
import Accordion from "../../../components/accordion";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import namedRoutes from "../../../settings/routes";
import { Fragment } from "preact";

import {
  FourPlusEightTable,
  XT150Table,
  XT60Table,
} from "./portCompatibilityTables";

const headerInfo = lookupByUrl(namedRoutes.ecoflow.altCharger.gen2CableGuide);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      short
      positionY={70}
      background="/images/products/ecoflow/alternator-charger-plus-1000/plug-in.webp"
      titlePrefix="EcoFlow Alternator Charger"
      title="Cable & Port Connection Guide"
      subTitle="600W and Plus 1000W Models"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          Connecting an <strong>EcoFlow Alternator Charger Plus 1000</strong> or{" "}
          <strong>Alternator Charger 600</strong> requires the correct cable or
          adapter to work with your specific power station. EcoFlow uses three
          port types: 4+8, XT150, and XT60. This guide explains how to identify
          your port and choose the correct cable or adapter.
        </p>

        <p>
          Many users run into problems because their Alternator Charger does not
          connect or the included cable does not match their model. This is
          especially common with the <strong>DELTA 3 Max Plus</strong>,{" "}
          <strong>DELTA 3 Ultra Plus</strong>, <strong>DELTA Pro</strong>, and{" "}
          <strong>DELTA Pro 3</strong> that require the Custom Fit (4+8) output
          cable or the Smart Generator Adapter. For the prior generation
          chargers, these accessories are often{" "}
          <strong>sold out or backordered</strong>, and some third-party stores
          list them as available but cancel orders later.
        </p>

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Three Port Types</SlugLink>
                  </li>
                  <li>
                    <SlugLink>4+8 Expansion Port</SlugLink>
                  </li>
                  <li>
                    <SlugLink>XT150 Expansion Port</SlugLink>
                  </li>
                  <li>
                    <SlugLink>XT60 Solar Port</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Alternator Charger Models</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Power Station Recommendations</SlugLink>
                  </li>
                </ol>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <h2>Three Port Types</h2>
        <p>
          Whenever possible, use the <strong>4+8</strong> or{" "}
          <strong>XT150</strong> port. These ports support full charging rate
          and all 3-in-1 features such as reverse charging and adjustable
          charging rate. All EcoFlow power stations can use the XT60 output
          cable, but this limits charging to 720W maximum and disables advanced
          features.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              large
              affiliateData={affiliateMerchants.ecoflow}
              img="/images/products/ecoflow/assorted/ports/4+8.webp"
              title="4+8 Expansion Port"
              description={
                <Fragment>
                  <div style="margin-bottom:6px;">
                    Featured on DELTA 3 Max Plus, DELTA 3 Ultra Plus, DELTA Pro,
                    and DELTA Pro 3.
                  </div>
                  <SlugLink text="4+8 Expansion Port">Jump to section</SlugLink>
                </Fragment>
              }
            />
          </li>
          <li>
            <PhotoListItem
              large
              affiliateData={affiliateMerchants.ecoflow}
              img="/images/products/ecoflow/assorted/ports/xt150.webp"
              title="XT150 Expansion Port"
              description={
                <Fragment>
                  <div style="margin-bottom:6px;">
                    Featured on some DELTA series power stations (full list
                    below).
                  </div>
                  <SlugLink text="XT150 Expansion Port">
                    Jump to section
                  </SlugLink>
                </Fragment>
              }
            />
          </li>
          <li>
            <PhotoListItem
              large
              affiliateData={affiliateMerchants.ecoflow}
              img="/images/products/ecoflow/assorted/ports/xt60.webp"
              title="XT60 Solar Port"
              description={
                <Fragment>
                  <div style="margin-bottom:6px;">
                    Featured on all EcoFlow and many third-party power stations.
                  </div>
                  <SlugLink text="XT60 Solar Port">Jump to section</SlugLink>
                </Fragment>
              }
            />
          </li>
        </ul>

        <Warning prefix="Gen 1 Cables">
          Second-generation (600W/1000W) chargers use a redesigned, proprietary
          pin configuration. Legacy first-generation (500W/800W) cables are
          physically incompatible and cannot be reused with Gen 2 charger
          hardware.
        </Warning>
      </Center>
      <Center>
        <SlugHeading tag="h2">4+8 Expansion Port</SlugHeading>

        <Image
          src="/images/products/ecoflow/assorted/ports/4+8.webp"
          alt="4+8 Expansion Port"
          showAlt
          class={style.floatLeft}
        />

        <p>
          Some of EcoFlow's premium power stations use a proprietary 4+8 pin
          expansion battery port instead of the older style XT150 port. These
          models cannot use XT150 directly, so the Custom Fit (4+8) output cable
          or the Smart Generator Adapter is required to connect the Alternator
          Charger.
        </p>

        <Accordion
          disableContentPadding
          items={[
            {
              heading: "See power stations with 4+8 ports",
              content: (
                <Fragment>
                  <FourPlusEightTable />
                </Fragment>
              ),
            },
          ]}
        />
        <p>
          Because these power stations don't have a standard XT150 port, you
          have two options to connect your EcoFlow Alternator Charger and retain
          full charging rate and all advanced features:
        </p>

        <ol>
          <li>
            <strong>The 4+8 Output Cable (Recommended):</strong> The 4+8 Output
            Cable replaces the stock cable entirely and plugs directly into the
            4+8 port. It works with both the 1000W and 600W chargers.
            <ul class="none">
              <li>
                <PhotoListItem
                  ffiliateData={affiliateMerchants.ecoflow}
                  href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-4-8output"
                  img="/images/products/ecoflow/accessories/output-cable-4+8.webp"
                  title="4+8 output cable"
                  titleSuffix="(Japan Link)"
                  description={
                    <Fragment>
                      Sold separately <br />
                      Supports: charging (full rate), reverse charging, battery
                      maintenance
                    </Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
            </ul>
          </li>
          <li>
            <strong>Smart Generator Adapter + XT150 Output Cable:</strong> If
            you already own an XT150 output cable (included with 1000W model),
            all you need is the Smart Generator Adapter. The accessory adapter
            plugs into the 4+8 port and converts it into an XT150 port that your
            cable can plug into. The XT150 cable can be purchased separately if
            you own the 600W model.
            <ul class="none">
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
                  img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
                  title="Smart Generator Adapter"
                  description={
                    <Fragment>
                      Allows XT150 cable to connect to 4+8 port
                    </Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt150output-new-1m"
                  img="/images/products/ecoflow/alternator-charger-plus-1000/xt150-cable.webp"
                  title="XT150 output cable"
                  titleSuffix="(Japan Link)"
                  description={
                    <Fragment>
                      Included in: 1000W model <br />
                      Supports: charging (full rate), reverse charging, battery
                      maintenance
                    </Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://au.ecoflow.com/products/ecoflow-alternator-charger-plus-1000"
                  img="/images/products/ecoflow/alternator-charger-plus-1000/front.webp"
                  title="Alternator Charger Plus 1000"
                  titleSuffix="(Australia Link)"
                  description={
                    <Fragment>Cable included: XT150 output cable</Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
            </ul>
          </li>
        </ol>

        <Warning prefix="Important">
          The Smart Generator Adapter is often out of stock, backordered, or
          temporarily unavailable. Unsure how well EcoFlow will stock the new
          4+8 Output Cable. Some third party stores have listed these as
          available but later cancel the order or delay shipping due to limited
          stock.
        </Warning>

        <h3>Expansion Port Already Used</h3>
        <p>
          If you own a <strong>DELTA 3 Max Plus</strong> or{" "}
          <strong>DELTA 3 Ultra Plus</strong> and you are already using your
          expansion port for an extra battery or smart generator, you will run
          out of ports. To run both the extra battery and the Alternator Charger
          simultaneously, you must purchase the EcoFlow{" "}
          <strong>Extra Battery Expansion Adapter</strong> to act as a splitter.
        </p>
        <PhotoListItem
          affiliateData={affiliateMerchants.ecoflow}
          href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
          img="/images/products/ecoflow/accessories/extra-battery-expansion-adapter.webp"
          title="Extra Battery Expansion Adapter"
          description={
            <Fragment>
              Connects alternator charger + one expansion battery <br />
              Supported models: DELTA 3 Max Plus / Ultra Plus
            </Fragment>
          }
          buttonText="EcoFlow"
          buttonIcon="EcoFlow"
        />
        <p>
          The <strong>DELTA Pro</strong> and <strong>DELTA Pro 3</strong> both
          have two expansion ports so you can connect one extra battery and an
          alternator charger easily. These models don't support the EcoFlow
          Extra Battery Expansion Adapter.
        </p>

        <Warning icon={false} blue>
          <strong>Alternative Option:</strong> If you don't want to buy these
          specific accessory cables or adapters, they are not available to
          purchase, or if your port is already in use and you don't want to buy
          the splitter, you can bypass the Extra Battery port entirely and use
          the XT60 Solar Port. See below for more information on limitations.
        </Warning>

        <p class="caption-left">
          <strong>Feedback to EcoFlow:</strong> Please start producing a version
          of the Alternator Charger Plus 1000 that{" "}
          <strong>includes the 4+8 output cable</strong>. It's a waste of
          resources for people to purchase this cable separately and results in
          frustrated customers.
        </p>
      </Center>

      <Center>
        <SlugHeading tag="h2">XT150 Expansion Port</SlugHeading>
        <Image
          src="/images/products/ecoflow/assorted/ports/xt150.webp"
          alt="XT150 Expansion Port"
          showAlt
          class={style.floatLeft}
        />

        <p>
          Many of EcoFlow's power stations feature an XT150 expansion battery
          port. This makes them the easiest units to connect and retain full
          charging rate and all 3-in-1 smart features as the 1000W model
          includes the cable. Utilizing this dedicated battery port allows you
          to maximize charging efficiency without occupying your standard solar
          ports.
        </p>
        <Accordion
          disableContentPadding
          items={[
            {
              heading: "See power stations with XT150 ports",
              content: (
                <Fragment>
                  <XT150Table />
                </Fragment>
              ),
            },
          ]}
        />

        <ul>
          <li>
            <strong>XT150 Output Cable:</strong> You simply run the XT150 Output
            Cable from the Alternator Charger directly into the Extra Battery
            port on the side of your power station.
            <ul class="none">
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt150output-new-1m"
                  img="/images/products/ecoflow/alternator-charger-plus-1000/xt150-cable.webp"
                  title="XT150 output cable"
                  titleSuffix="(Japan Link)"
                  description={
                    <Fragment>
                      Included in: 1000W model <br />
                      Supports: charging (full rate), reverse charging, battery
                      maintenance
                    </Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://au.ecoflow.com/products/ecoflow-alternator-charger-plus-1000"
                  img="/images/products/ecoflow/alternator-charger-plus-1000/front.webp"
                  title="Alternator Charger Plus 1000"
                  titleSuffix="(Australia Link)"
                  description={
                    <Fragment>Cable included: XT150 output cable</Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
            </ul>
          </li>
        </ul>

        <h3>Expansion Port Already Used</h3>
        <p>
          Unlike some premium 4+8 pin models, EcoFlow does not manufacture a
          splitter for the older style XT150 port.
        </p>
        <ul>
          <li>
            <strong>Single-Port Stations:</strong> Popular mid-tier models like
            the DELTA 3 Plus and DELTA 2 only feature a single XT150 expansion
            port. If you already have an Extra Battery plugged into it, your
            port is physically occupied so you will need to use the XT60
            connection instead.
          </li>
          <li>
            <strong>Dual-Port Stations:</strong> Some models like the DELTA 2
            Max feature two separate XT150 ports, meaning you can natively plug
            in an extra battery and an Alternator Charger side-by-side.
          </li>
        </ul>

        <h3>600W Charger Model</h3>
        <p>
          If you buy the 600W charger for an XT150 compatible power station, you
          have the option to purchase the standalone XT150 Output Cable and
          access features such as adjustable charging rate and reverse charging.
        </p>

        <Warning icon={false} blue>
          <strong>Alternative Option:</strong> If you don't want to buy these
          specific accessory cables, they are not available to purchase, or if
          your port is already in use, you can bypass the Extra Battery Port
          entirely and use the XT60 Solar Port. See below for more information
          on limitations.
        </Warning>
      </Center>

      <Center>
        <SlugHeading tag="h2">XT60 Solar Port</SlugHeading>
        <Image
          src="/images/products/ecoflow/assorted/ports/xt60.webp"
          alt="XT60 Solar Port"
          showAlt
          class={style.floatLeft}
        />
        <p>
          An XT60 solar port is featured on all{" "}
          <Link href={namedRoutes.ecoflow.vanlife}>EcoFlow power stations</Link>{" "}
          and even many{" "}
          <Link href={namedRoutes.powerSystems}>
            third-party power stations
          </Link>
          . If your power station does not have an expansion battery port, or
          you choose not to use it, you must route power through the standard
          XT60 solar input. This cable is included in the 600W Alternator
          Charger model.
        </p>
        <p>
          If the 4+8 cable or Smart Generator Adapter is unavailable, out of
          stock, or delayed due to backorders, the XT60 port becomes the
          temporarily fallback accessory for most users.
        </p>

        <Accordion
          disableContentPadding
          items={[
            {
              heading: "See power stations with XT60 ports",
              content: (
                <Fragment>
                  <XT60Table />
                </Fragment>
              ),
            },
          ]}
        />

        <ul>
          <li>
            <strong>XT60 Output Cable:</strong> The 600W Alternator Charger
            includes this cable in the box. If you have the 1000W model, you
            will need to purchase the XT60 output cable separately.
            <ul class="none">
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output"
                  img="/images/products/ecoflow/alternator-charger-600/xt60-cable.webp"
                  title="XT60 output cable"
                  titleSuffix="(EcoFlow or Third-Party / Japan Link)"
                  description={
                    <Fragment>
                      Included in: 600W model <br />
                      Supports: charging (720W maximum)
                    </Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
              <li>
                <PhotoListItem
                  affiliateData={affiliateMerchants.ecoflow}
                  href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-600"
                  img="/images/products/ecoflow/alternator-charger-600/front.webp"
                  title="Alternator Charger 600"
                  titleSuffix="(Japan Link)"
                  description={
                    <Fragment>Cable included: XT60 output cable</Fragment>
                  }
                  buttonText="EcoFlow"
                  buttonIcon="EcoFlow"
                />
              </li>
            </ul>
          </li>
        </ul>

        <h3>Solar Ports Already Used</h3>
        <p>
          If your solar ports are already used and this is your final remaining
          connection option, you have a few ways forward without{" "}
          <Link href={namedRoutes.ecoflow.vanlife}>
            upgrading your power station
          </Link>
          :
        </p>
        <ul>
          <li>
            <strong>Manual cable swapping:</strong> You will have to manually
            unplug your solar array and plug in the Alternator Charger when
            driving, then swap them back to use solar. To prevent wearing out
            your power station’s internal port from frequent unplugging,
            consider using a short{" "}
            <Link
              affiliate="https://amzn.to/4nzlm6E"
              href="https://www.amazon.com/dp/B0DPQFWDKW"
            >
              XT60 extension cord
            </Link>
            .
          </li>
          <li>
            <strong>Alternator charger with built-in solar:</strong> If you want
            a setup without manual swapping, consider an{" "}
            <Link href={namedRoutes.alternatorChargers}>
              alternator charger
            </Link>
            , such as the <strong>EcoFlow Alternator Charger Plus 1000</strong>,
            that also accepts solar input. EcoFlow’s version of this charger
            isn't expected to launch in North America until 2027, though it is
            already available in select international markets. See my{" "}
            <Link href={namedRoutes.ecoflow.altCharger.gen2}>
              EcoFlow Alternator Charger Plus 1000 review
            </Link>{" "}
            for more details.
          </li>
        </ul>

        <Warning prefix="Important">
          There is no safe XT150 to XT60 adapter. Trying to adapt between them
          could damage your power station.
        </Warning>

        <h3>Downsides of using XT60 Port</h3>
        <p>
          By using the XT60 port, you are essentially tricking the power station
          into treating the Alternator Charger like a solar panel.
        </p>
        <ul>
          <li>
            Capped at 720W maximum (even with 1000W model) or maximum your power
            station allows (check compatibility above or in{" "}
            <Link href={namedRoutes.ecoflow.altCharger.gen2Compatibility}>
              my compatibility guide
            </Link>
            )
          </li>
          <li>Cannot adjust the charging rate</li>
          <li>
            Cannot use reverse charging, emergency reverse charging, or battery
            maintenance mode
          </li>
        </ul>

        <p class="caption-left">
          <strong>Note:</strong> the XT60 output cable actually uses an XT60i
          connector which signals to the power station to allow a higher
          charging rate than the{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/car-charging-cable?variant=39308519604297"
          >
            100W car charging cable
          </Link>
          .
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Alternator Charger Models</SlugHeading>

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                <th>Plus 1000</th>
                <th>600</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-plus-1000/front.webp"
                    alt="EcoFlow Alternator Charger Plus 1000"
                    showAlt
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-600/front.webp"
                    alt="EcoFlow Alternator Charger 600"
                    showAlt
                    maxHeight={100}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://au.ecoflow.com/products/ecoflow-alternator-charger-plus-1000"
                      icon="EcoFlow"
                    >
                      Australia
                    </Button>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-plus-1000"
                      icon="EcoFlow"
                    >
                      Japan
                    </Button>
                    {/* <Button
                      size="small"
                      affiliate="https://amzn.to/3N4b6p1"
                      href="https://www.amazon.com/dp/B0DSBZVPS6/"
                      secondary
                      icon="Amazon"
                    >
                      Amazon
                    </Button> */}
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-600"
                      icon="EcoFlow"
                    >
                      Japan
                    </Button>
                    {/* <Button
                      size="small"
                      affiliate="https://amzn.to/4jzlwsP"
                      href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                      secondary
                      icon="Amazon"
                    >
                      Amazon
                    </Button> */}
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                <td>
                  <NoWrap>TBD</NoWrap>
                </td>
                <td>
                  <NoWrap>TBD</NoWrap>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Cable Included</strong>
                </th>
                <td>
                  <strong>XT150</strong>
                </td>
                <td>
                  <strong>XT60</strong>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>
                    <NoWrap>XT150 /</NoWrap> <NoWrap>4+8 Cable</NoWrap>
                  </strong>
                </th>
                <td>
                  <div style="text-align:left;display:inline-block;">
                    <div>
                      <NoWrap>
                        Max Charge: <strong>1000W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Reverse Charge: <strong>1000W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Maintenance Mode: <strong>100W</strong>
                      </NoWrap>
                    </div>
                  </div>
                </td>
                <td>
                  <div style="text-align:left;display:inline-block;">
                    <div>
                      <NoWrap>
                        Max Charge: <strong>600W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Reverse Charge: <strong>600W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Maintenance Mode: <strong>100W</strong>
                      </NoWrap>
                    </div>
                  </div>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>XT60 Cable</strong>
                </th>
                <td>
                  <div style="text-align:left;display:inline-block;">
                    <div>
                      <NoWrap>
                        Max Charge: <strong>720W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Reverse Charge:{" "}
                        <span aria-label="No" title="No">
                          ❌
                        </span>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Maintenance Mode:{" "}
                        <span aria-label="No" title="No">
                          ❌
                        </span>
                      </NoWrap>
                    </div>
                  </div>
                </td>
                <td>
                  <div style="text-align:left;display:inline-block;">
                    <div>
                      <NoWrap>
                        Max Charge: <strong>600W</strong>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Reverse Charge:{" "}
                        <span aria-label="No" title="No">
                          ❌
                        </span>
                      </NoWrap>
                    </div>
                    <div>
                      <NoWrap>
                        Maintenance Mode:{" "}
                        <span aria-label="No" title="No">
                          ❌
                        </span>
                      </NoWrap>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </FixedTable>

        <p class="caption">
          4+8 output cable offers same functionality and charge rates as XT150
          output cable.
        </p>

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />

        <div class={style.section}>
          <h3>Detailed Review</h3>
          <p>
            Check out my full comparison review for all details on EcoFlow
            Alternator Chargers:
          </p>
          <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />

          <Accordion
            headingsAs="h3"
            items={[
              {
                heading: "Frequently asked questions (FAQ)",
                content: (
                  <Fragment>
                    <div class={style.faq}>
                      <div>
                        <h4>
                          Why won’t my EcoFlow Alternator Charger connect to my
                          power station?
                        </h4>
                        <p>
                          Connection issues almost always come from using the
                          wrong accessory cable or adapter for your port type.
                          EcoFlow uses three different ports (4+8, XT150, XT60),
                          and each requires a specific cable. If the charger
                          doesn’t connect, verify that the cable matches the
                          port on your power station and that the port isn’t
                          already in use.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Which Alternator Charger accessory cable do I need for
                          my EcoFlow model?
                        </h4>
                        <p>
                          Your required accessory cable depends on which ports
                          your power station has. Models with a 4+8 port need
                          the Custom Fit (4+8) cable or the Smart Generator
                          Adapter, XT150 models use the XT150 cable, and all
                          models can fall back to XT60.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Why does my port not match the cable included with my
                          Alternator Charger?
                        </h4>
                        <p>
                          EcoFlow includes different cables depending on whether
                          you purchased the 600W or 1000W model. Some power
                          stations require a different accessory cable than the
                          one included, especially newer 4+8 models. If the
                          included cable doesn’t match your port, you’ll need
                          the correct accessory cable or adapter for your
                          specific model.
                        </p>
                      </div>
                      <div>
                        <h4>
                          How do I know if my power station is compatible with
                          the 4+8, XT150, or XT60 cable?
                        </h4>
                        <p>
                          Check the physical ports located on the back or side
                          of your power station and match them to the cable
                          type. EcoFlow labels ports clearly, and each port
                          shape is unique, so you can identify compatibility
                          visually. If your model has multiple ports, the 4+8 or
                          XT150 port always provides the best performance.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Why am I limited to 720W even when using the 1000W
                          Alternator Charger?
                        </h4>
                        <p>
                          Charging is capped at 720W when using the XT60 port
                          because the Alternator Charger lowers it to a safe
                          level required for the cable. To reach the full 1000W
                          rate, you must use the XT150 cable or the Custom Fit
                          (4+8) cable with a compatible model. Port
                          compatibility determines the maximum charging rate.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Does the cable I use affect charging performance or
                          features?
                        </h4>
                        <p>
                          Yes, the cable determines both the maximum charging
                          rate and which features are available. XT150 and 4+8
                          cables support full or adjustable wattage, reverse
                          charging, and battery maintenance modes, while XT60 is
                          limited to 720W and disables advanced features.
                          Choosing the correct accessory cable ensures full
                          functionality.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Why can’t I use reverse charging or battery
                          maintenance modes?
                        </h4>
                        <p>
                          These features only work when using the XT150 cable or
                          the Custom Fit (4+8) cable with a compatible EcoFlow
                          model. XT60 connections do not support reverse
                          charging or maintenance modes because the power
                          station treats the input as solar. If these features
                          are missing, the cable and port are the limiting
                          factor.
                        </p>
                      </div>
                      <div>
                        <h4>
                          What if my battery expansion port is already in use?
                        </h4>
                        <p>
                          If your expansion port is occupied by an extra battery
                          or smart generator, you must either use a splitter
                          (only available for the DELTA 3 Max Plus and DELTA 3
                          Ultra Plus) or switch to the XT60 port. XT60 allows
                          charging but limits performance and disables advanced
                          features. Dual‑port models like DELTA 2 Max and DELTA
                          Pro avoid this issue entirely.
                        </p>
                      </div>
                      <div>
                        <h4>What if my solar ports are already in use?</h4>
                        <p>
                          If your solar ports are occupied, you’ll need to
                          manually swap cables or use an alternator charger that
                          supports solar passthrough. The Alternator Charger
                          Plus 1000 supports solar passthrough, but it is not
                          yet available in North America.
                        </p>
                      </div>
                      <div>
                        <h4>
                          Can I use the newer Gen 1 (500W/8000W) cables with my
                          Gen 2 Alternator Charger?
                        </h4>
                        <p>
                          No. The Gen 2 (600W and 1000W) Alternator Chargers
                          utilize a completely different proprietary pin
                          configuration and physical port housing than the older
                          Gen 1 models.
                        </p>
                      </div>
                    </div>
                  </Fragment>
                ),
              },
            ]}
          />
        </div>

        <div class={style.section}>
          <h3>Compare Other Brands</h3>
          <p>
            A broader comparison of alternator chargers across multiple brands,
            including EcoFlow, BLUETTI, Jackery, Anker, and more.
          </p>
          <ReviewItem href={namedRoutes.alternatorChargers} />
        </div>
      </Center>
      <Center>
        <SlugHeading tag="h2">Power Station Recommendations</SlugHeading>
        <p>
          Check out my top power station recommendations for EcoFlow and other
          popular brands:
        </p>
        <ReviewItem href={namedRoutes.ecoflow.vanlife} />

        <ReviewItem href={namedRoutes.powerSystems} />

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>

      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem
              href={namedRoutes.ecoflow.altCharger.gen2Compatibility}
            />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      {/* <StickyButtons
        title="EcoFlow Alternator Chargers"
        description="Plus 1000 and 600"
        options={[
          {
            text: "View on EcoFlow",
            icon: "EcoFlow",
            options: [
              {
                text: "Alternator Charger Plus 1000",
                affiliateData: affiliateMerchants.ecoflow,
                href: "",
              },
              {
                text: "Alternator Charger 600",
                affiliateData: affiliateMerchants.ecoflow,
                href: "",
              },
            ],
          },
          {
            text: "View on Amazon",
            icon: "Amazon",
            secondary: true,
            options: [
              {
                text: "Alternator Charger Plus 1000",
                affiliate: "",
                href: "",
              },
              {
                text: "Alternator Charger 600",
                affiliate: "",
                href: "",
              },
            ],
          },
        ]}
      /> */}
    </Editorial>
  </div>
);

export default Page;
